import { useCallback, useMemo, useState } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { FilterListRoundedIcon } from "@l2r-front/l2r-icons"
import { useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"

import { AddDocumentButton, AddDocumentButtonMobile } from "../../components/AddDocumentButton"
import { DocumentEditModal } from "../../components/DocumentEditModal"
import { DocumentLinearLocationSelector } from "../../components/DocumentLinearLocationSelector"
import { FILTERS } from "../../constants/filters"
import { I18N_NAMESPACE } from "../../constants/i18n"
import { API_MODULE_DOCUMENT, NETWORK_ATTACHMENT } from "../../constants/modules"
import { DocumentAddForm } from "../../forms/DocumentAddForm"

import { DocumentSidebarContent } from "../DocumentSidebarContent"

import * as Styled from "./DocumentSidebar.styled"

export function DocumentSidebar() {

    const { t } = useTranslation([I18N_NAMESPACE])
    const isMobile = useIsMobileDevice()
    const [displayFilters, setDisplayFilters] = useState(false)
    const readOnly = useRoleIsReadOnly(null, API_MODULE_DOCUMENT)
    const [documentLocation, setDocumentLocation] = useState(null)
    const [linearLocationSelectorType, setLinearLocationSelectorType] = useState(null)
    const [documentAddModalType, setDocumentAddModalType] = useState(false)

    const closeFilters = () => setDisplayFilters(false)
    const toggleFilter = () => setDisplayFilters(displayFilters => !displayFilters)

    const closeLinearLocationModal = useCallback(() => {
        setLinearLocationSelectorType(null)
    }, [])

    const handleAddDocumentMenuSelect = useCallback((selectedType) => {
        if (selectedType === NETWORK_ATTACHMENT) {
            setDocumentAddModalType(selectedType)
        } else {
            setLinearLocationSelectorType(selectedType)
        }
    }, [])

    const handleSelectLinearLocation = useCallback((location) => {
        setDocumentLocation(location)
        if (location) {
            setDocumentAddModalType(linearLocationSelectorType)
        }
        setLinearLocationSelectorType(null)
    }, [linearLocationSelectorType])

    const DesktopFilterComponent = useMemo(() => <Styled.DisplayedFiltersWrapper>
        <Styled.IconButton onClick={toggleFilter} id="filters-button">
            <FilterListRoundedIcon color="primary" />
        </Styled.IconButton>
    </Styled.DisplayedFiltersWrapper>, [])

    return (
        <Styled.BottomSheetContent
            id="document-sidebar"
            title={t(I18N_NAMESPACE, "containers.documentSidebar.title")}
            titleBottomComponent={!isMobile ? <Styled.DocumentFilterList enabledFilters={FILTERS} /> : null}
            titleSideComponent={<Styled.SideComponentContainer>
                <Styled.DocumentsCountBadge />
                {
                    !isMobile && DesktopFilterComponent
                }
                {!readOnly && !isMobile && <AddDocumentButton onAddDocumentMenuSelected={handleAddDocumentMenuSelect} />}
                {!readOnly && isMobile && <AddDocumentButtonMobile onAddDocumentMenuSelected={handleAddDocumentMenuSelect} />}
            </Styled.SideComponentContainer>}
        >
            <Styled.BackdropFilter
                id="backdrop-filters"
                onClick={toggleFilter}
                open={displayFilters}
            />
            <Styled.Wrapper>
                <Styled.DocumentFiltersForm close={closeFilters} displayFilters={displayFilters} />
            </Styled.Wrapper>
            <DocumentSidebarContent />
            {!!linearLocationSelectorType && <DocumentLinearLocationSelector
                onClose={closeLinearLocationModal}
                onLinearLocationSelected={handleSelectLinearLocation}
                selectedLinearLocation={documentLocation}
                selectionType={linearLocationSelectorType}
            />}
            {documentAddModalType && <DocumentEditModal
                documentLocation={documentLocation}
                open={documentAddModalType}
                onClose={() => {
                    setDocumentLocation(null)
                    setDocumentAddModalType(false)
                }}
                selectionType={documentAddModalType}
                FormComponent={DocumentAddForm}
            />}
        </Styled.BottomSheetContent>
    )
}
